import React from "react";
import { useTranslation } from "react-i18next";
import { UserIcon, EditIcon } from "../assets/constants/Icons";
import { REGEXP_EMAIL, SUCCESS, TOAST_ERROR, TOAST_SUCCESS } from "../assets/constants/Constants";
import { UpdateUserProfile } from "../api/api";
import ShowToast from "./ToastComponent";
import UserDetailsDialog from "./UserDetailsDialog";
import { LOCAL_STORAGE, USER_INFO } from "../assets/constants/BrowserStorageKeys";
import { storeSecureItemInSpecifiedStorage } from "../lib/BrowserStorageAccessMiddleware";

export default function UserDetailsComponent({
  userInfo,
  onUserInfoUpdate,
  isAuthenticatedUser = false
}) {
  const { t } = useTranslation();

  const [isEditOpen, setIsEditOpen] = React.useState(false);
  const [formData, setFormData] = React.useState({
    first_name: userInfo?.first_name || "",
    last_name: userInfo?.last_name || "",
    username: userInfo?.username || "",
    email: userInfo?.email || "",
    mobile_number: userInfo?.mobile_number || ""
  });
  const [formErrors, setFormErrors] = React.useState({});
  const [submitAttempted, setSubmitAttempted] = React.useState(false);

  React.useEffect(() => {
    setFormData({
      first_name: userInfo?.first_name || "",
      last_name: userInfo?.last_name || "",
      username: userInfo?.username || "",
      email: userInfo?.email || "",
      mobile_number: userInfo?.mobile_number || ""
    });
  }, [userInfo]);

  const validateUserDetails = (details) => {
    const errors = {};

    if (!details.first_name?.trim()) {
      errors.first_name = t("Error_First_name_required");
    }
    if (!details.last_name?.trim()) {
      errors.last_name = t("Error_Last_name_required");
    }
    if (!details.email?.trim()) {
      errors.email = t("Error_Email_required");
    } else if (!REGEXP_EMAIL.test(details.email)) {
      errors.email = t("LoginEmailError");
    }

    return errors;
  };

  const handleChangeUserDetails = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value
    }));

    setFormErrors((prev) => ({
      ...prev,
      [field]: ""
    }));
  };

  const resetFormState = () => {
    setSubmitAttempted(false);
    setFormErrors({});
    setFormData({
      first_name: userInfo?.first_name || "",
      last_name: userInfo?.last_name || "",
      username: userInfo?.username || "",
      email: userInfo?.email || "",
      mobile_number: userInfo?.mobile_number || ""
    });
  };

  const handleCloseUserDetailsDialog = () => {
    setIsEditOpen(false);
    resetFormState();
  };

  const handleSubmitUserDetails = async () => {
    setSubmitAttempted(true);

    const errors = validateUserDetails(formData);
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const payload = {
        ...formData,
        mobile_number: `${formData.mobile_number || ""}`.replace(/^\+/, "")
      };
      const response = await UpdateUserProfile(payload);

      if (response?.status === SUCCESS) {
        const updatedUserInfo = {
          ...userInfo,
          ...formData,
          mobile_number: formData.mobile_number?.startsWith("+")
            ? formData.mobile_number
            : formData.mobile_number
              ? `+${formData.mobile_number}`
              : ""
        };

        storeSecureItemInSpecifiedStorage(LOCAL_STORAGE, USER_INFO, updatedUserInfo);
        if (onUserInfoUpdate) {
          onUserInfoUpdate(updatedUserInfo);
        }

        ShowToast(t("Success_User_Details_Update"), TOAST_SUCCESS);
        setIsEditOpen(false);
        setSubmitAttempted(false);
        setFormErrors({});
        setFormData({
          first_name: updatedUserInfo.first_name || "",
          last_name: updatedUserInfo.last_name || "",
          username: updatedUserInfo.username || "",
          email: updatedUserInfo.email || "",
          mobile_number: updatedUserInfo.mobile_number || ""
        });
        return;
      }

      ShowToast(t(response?.message || "something_went_wrong_text"), TOAST_ERROR);
    } catch (error) {
      console.error("Failed to update user profile:", error);
      ShowToast(t("something_went_wrong_text"), TOAST_ERROR);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      month: "short",
      year: "numeric"
    });
  };

  const displayName = [userInfo?.first_name, userInfo?.last_name]
    .filter(Boolean)
    .join(" ")
    || userInfo?.username
    || "-";
  const isAnonymousUser = !isAuthenticatedUser;

  return (
    <>
      <div className="user-card">
        <div className="user-card__top">
          <UserIcon />

          <div className="user-info">
            <div className="name-row">
              <h3>{displayName}</h3>

              {!isAnonymousUser && (
                <div
                  className="edit-icon"
                  onClick={() => setIsEditOpen(true)}
                >
                  <EditIcon />
                </div>
              )}
            </div>

            <p>{userInfo?.email}</p>
          </div>
        </div>

        <hr />
      {!isAnonymousUser && 
        <div className="user-card__bottom">
          <div className="info-row">
            <span className="label">{t("phone_label")}</span>
            <span className="value">{userInfo?.mobile_number}</span>
          </div>

          <div className="info-row">
            <span className="label">{t("member_since_label")}</span>
            <span className="value">
              {formatDate(userInfo?.date_joined)}
            </span>
          </div>
        </div>
        }
      </div>

      <UserDetailsDialog
        open={isEditOpen}
        onClose={handleCloseUserDetailsDialog}
        userDetails={formData}
        handleChange={handleChangeUserDetails}
        handleSubmit={handleSubmitUserDetails}
        error={submitAttempted ? formErrors : {}}
      />
    </>
  );
}
